import { SidebarMenuButton } from "@babackapp/ui/components/sidebar";
import {
  ChangelogFeedMenu,
  Markdown,
  useChangelogFeed,
} from "@squeletteapp/widget-react";
import { ArrowUpRight, Gift } from "lucide-react";

import { squeletteKey } from "./squelette-key";

/**
 * The last announcements, behind a sidebar entry.
 *
 * `ChangelogFeedMenu` rather than `SqueletteChangelogFeed`, which wires the
 * same menu in one line: that wrapper resolves the public board address from
 * the API and hands every announcement its page there, so the window ends on
 * a "see on the board" button. No href is passed here, and the window keeps
 * what it is worth reading in place.
 *
 * The menu opens above the button, left edges aligned: the card is wider
 * than the sidebar, so it spreads over the page rather than off the screen.
 * Its colors come from the `--sq-*` tokens set on `:root` in index.css: the menu
 * is portaled to `body`, where a wrapper around the trigger would not reach
 * it.
 */
export default function ChangelogMenu() {
  // The hook is in a child so it never runs without a key: it would fire its
  // two calls anyway, and answer nothing.
  if (!squeletteKey) return null;

  return <Menu apiKey={squeletteKey} />;
}

function Menu({ apiKey }: { apiKey: string }) {
  const feed = useChangelogFeed({ key: apiKey });

  return (
    <ChangelogFeedMenu
      entries={feed.entries}
      body={(entry) => <Markdown text={feed.bodyOf(entry) ?? ""} />}
      onOpen={feed.read}
      onReact={feed.vote ?? undefined}
      reactedOf={(entry) => entry.viewer_vote === 1}
      reactionsOf={(entry) => entry.vote_count}
      // A menu opened before the response lands must say something, otherwise
      // the click on its button looks like it did nothing. The empty sentence
      // of a workspace that has published nothing would be a lie half the
      // time.
      emptyLabel={feed.loading ? "Chargement des annonces..." : undefined}
      theme="light"
      side="top"
      align="start"
    >
      <SidebarMenuButton>
        <Gift className="size-4" />
        <span className="flex-1">Nouveautés</span>
        <ArrowUpRight className="size-4" />
      </SidebarMenuButton>
    </ChangelogFeedMenu>
  );
}
